import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description, variant = 'success', duration = 4500 }) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id, title, description, variant }]);
      setTimeout(() => remove(id), duration);
    },
    [remove]
  );

  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className="fixed z-[100] bottom-6 right-6 flex flex-col gap-3 max-w-[92vw] sm:max-w-sm" data-testid="toast-region">
        {toasts.map((t) => (
          <div
            key={t.id}
            data-testid={`toast-${t.variant}`}
            className="bg-offwhite border border-moss/30 shadow-[0_12px_40px_rgba(26,59,43,0.12)] rounded-2xl p-4 flex gap-3 animate-fadeUp"
          >
            <div className="mt-0.5">
              {t.variant === 'error' ? (
                <AlertCircle className="w-5 h-5 text-red-700" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-pond" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-display text-lg leading-tight text-pond">{t.title}</div>
              {t.description && (
                <div className="text-sm text-muted mt-0.5">{t.description}</div>
              )}
            </div>
            <button
              onClick={() => remove(t.id)}
              className="text-muted hover:text-pond"
              aria-label="Close toast"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
